import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button } from "antd";
import {
  BankOutlined,
  CloseOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { createCompany } from "../../../../services/companyService";
import { useMessageApi } from "../../../../shared/providers/MessageProvider";
import useBodyScrollLock from "../../../../hooks/useBodyScrollLock";
import "./CompanyFormModal.scss";

/**
 * Company Form Modal Component
 * Simple and modern modal for creating new companies
 *
 * @param {boolean} visible - Controls modal visibility
 * @param {Object} editingCompany - Company object being edited (null for create mode)
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onSuccess - Callback when company is successfully created
 * @param {boolean} canCreate - Whether user has permission to create companies
 */
const CompanyFormModal = ({
  visible,
  editingCompany,
  onClose,
  onSuccess,
  canCreate = false,
}) => {
  const message = useMessageApi();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Lock body scroll when modal is open
  useBodyScrollLock(visible);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      if (editingCompany) {
        form.setFieldsValue({
          name: editingCompany.name,
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, editingCompany, form]);

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (!canCreate) {
        message.error("You don't have permission to create companies");
        return;
      }

      const values = await form.validateFields();
      setLoading(true);

      await createCompany(values.name.trim(), message);
      message.success("Company created successfully", 2.5);
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error?.errorFields) {
        // Validation error - already shown by form
        return;
      }
      console.error("Error saving company:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleClose}
      footer={null}
      className="company-form-modal"
      width={520}
      centered={true}
      closeIcon={<CloseOutlined className="close-icon" />}
      maskClosable={false}
      keyboard={true}
      destroyOnClose={true}
      transitionName=""
      maskTransitionName=""
      getContainer={false}
    >
      {/* Modal Header */}
      <div className="modal-header">
        <div className="header-content">
          <h2 className="header-title">Create New Company</h2>
        </div>
      </div>

      {/* Modal Body */}
      <div className="modal-body">
        <Form
          form={form}
          layout="vertical"
          className="company-form"
          requiredMark={false}
        >
          <Form.Item
            label="Company Name"
            name="name"
            rules={[
              { required: true, message: "Company name is required" },
              { min: 2, message: "Company name must be at least 2 characters" },
              { max: 100, message: "Company name must not exceed 100 characters" },
            ]}
          >
            <Input
              prefix={<BankOutlined className="input-icon" />}
              placeholder="Enter company name"
              autoFocus
            />
          </Form.Item>
        </Form>
      </div>

      {/* Modal Footer */}
      <div className="modal-footer">
        <Button onClick={handleClose} className="cancel-btn" disabled={loading}>
          <span className="btn-content">
            <CloseCircleOutlined />
            <span>Cancel</span>
          </span>
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          className="submit-btn"
          disabled={loading}
        >
          {loading ? (
            <span className="btn-content">
              <ReloadOutlined className="spin-icon" />
              <span>Creating...</span>
            </span>
          ) : (
            <span className="btn-content">
              <CheckCircleOutlined />
              <span>Create Company</span>
            </span>
          )}
        </Button>
      </div>
    </Modal>
  );
};

export default CompanyFormModal;

